'use client';

import { TreeStore } from '@/components/treeSelector/treeStore';
import { TreeNode, TreeState } from '@/components/treeSelector/types/tree';
import { isEqual } from 'lodash-es';
import { use, useEffect, useMemo } from 'react';
import { StoreApi, UseBoundStore } from 'zustand';

export const useTreeNode = <Type = unknown, Payload = unknown>(
  nodeId: string,
  useStore: UseBoundStore<StoreApi<TreeStore<Type, Payload>>>,
) => {
  const node = useStore((state) => state.visibleNodeMap[nodeId]);
  const checkedState = useStore((state) => state.checked[nodeId] || false);
  const searchTerm = useStore((state) => state.searchTerm);
  const toggleNode = useStore((state) => state.toggleNode);

  return useMemo(() => {
    if (!node) return null;
    return { node, checkedState, searchTerm, toggleNode: () => toggleNode(nodeId) };
  }, [node, checkedState, searchTerm, toggleNode, nodeId]);
};

export const useRootNode = <Type = unknown, Payload = unknown>(
  useStore: UseBoundStore<StoreApi<TreeStore<Type, Payload>>>,
) => {
  const rootId = useStore((state) => state.visibleTree.id);
  return useTreeNode(rootId, useStore);
};

export const useSearchTerm = <Type = unknown, Payload = unknown>(
  useStore: UseBoundStore<StoreApi<TreeStore<Type, Payload>>>,
) => {
  return useStore((state) => state.searchTerm);
};

export const useLoadTreeDataFromPromise = <Payload = unknown, Type = unknown>(
  promise: Promise<TreeNode<Type, Payload>>,
  useStore: UseBoundStore<StoreApi<TreeStore<Type, Payload>>>,
  initialStateParsed: TreeState<Type, Payload>,
) => {
  const data = use(promise);
  const loadTree = useStore((state) => state.loadTree);
  const treeState = useStore((state) => state.tree);
  const hasHydrated = useStore((state) => state._hasHydrated);

  useEffect(() => {
    if (isEqual(initialStateParsed.tree, treeState) && hasHydrated) {
      return loadTree(data);
    }
  }, [treeState, loadTree, data, initialStateParsed.tree, hasHydrated]);
};
